import { useStore } from "@tanstack/react-form";
import { Search, UserCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { KioskStep } from "../../../../../components/kiosk-step";
import type { StepAction } from "../../../context";
import { useCheckInSend } from "../context";
import { CheckInPatientLookupForm } from "./check-in-patient-lookup-form";
import { CheckInPatientLookupResultsPanel } from "./check-in-patient-lookup-results-panel";
import { useCheckInPatientLookup } from "./use-check-in-patient-lookup";
import { useKioskPolicy } from "../../../../../lib/kiosk-policy/kiosk-policy-context";

/**
 * Manual patient lookup step for check-in when no card is available. The
 * search is the footer action; once the patient is found, "Das bin ich"
 * becomes the footer action and the search moves to the secondary slot
 * (KC-67).
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <PatientLookupStep />
 * ```
 */
export function PatientLookupStep() {
  const { t } = useTranslation();
  const send = useCheckInSend();
  const lookup = useCheckInPatientLookup();
  const kioskPolicy = useKioskPolicy();
  const creationAllowed =
    kioskPolicy.source === "current" &&
    kioskPolicy.patientCreationEnabled;
  const canSubmit = useStore(lookup.form.store, (state) => state.canSubmit);
  const submit = () => void lookup.form.handleSubmit();
  const found = lookup.searchState.status === "found";

  const searchAction: StepAction = {
    label: found
      ? t("self-service.check-in.patient-lookup.search-again")
      : t("self-service.check-in.patient-lookup.search"),
    icon: Search,
    onClick: submit,
    disabled: !canSubmit,
    busy: lookup.isSearching,
    testId: "frontdesk-patient-lookup-search-btn",
  };

  return (
    <KioskStep
      width="narrow"
      title={t("self-service.check-in.patient-lookup.title-v2")}
      lead={
        lookup.needsFirstname
          ? t("self-service.check-in.patient-lookup.subtitle-firstname")
          : t("self-service.check-in.patient-lookup.subtitle")
      }
      primaryAction={
        found
          ? {
              label: t("self-service.check-in.patient-lookup.confirm-button"),
              icon: UserCheck,
              onClick: lookup.handleConfirm,
              testId: "frontdesk-patient-lookup-confirm-btn",
            }
          : searchAction
      }
      secondaryAction={found ? { ...searchAction, disabled: false } : undefined}
    >
      <CheckInPatientLookupForm
        form={lookup.form}
        isSearching={lookup.isSearching}
        needsFirstname={lookup.needsFirstname}
        onSubmit={submit}
      />
      <div aria-live="polite" className="contents">
        <CheckInPatientLookupResultsPanel
          searchState={lookup.searchState}
          onReceptionHelp={() => send({ type: "RECEPTION_HELP" })}
          onRegisterPatient={creationAllowed ? lookup.handleRegister : undefined}
        />
      </div>
    </KioskStep>
  );
}
